import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Breadcrumb } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchNewsByCategory, fetchCategories } from '../store/slices/newsSlice';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { items, status, error, categories } = useAppSelector((state) => state.news);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryName, setCategoryName] = useState('');
  
  // Find current category
  const currentCategory = categories.find(cat => cat.slug === slug);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'd MMMM yyyy');
  };
  
  useEffect(() => {
    if (slug) {
      dispatch(fetchNewsByCategory({ 
        slug, 
        page: currentPage,
        page_size: 9 
      }));
    }
    dispatch(fetchCategories());
  }, [dispatch, slug, currentPage]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [slug]);

  // Set category name when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && slug) {
      const category = categories.find(cat => cat.slug === slug);
      if (category) {
        setCategoryName(category.name);
      } else {
        navigate('/404');
      }
    }
  }, [categories, slug, navigate]);
  
  // Loading state
  if (status === 'loading' || status === 'idle') {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }
  
  // Error state
  if (status === 'failed') {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || 'Произошла ошибка при загрузке данных'}
        </Alert>
      </Container>
    );
  }
  
  // No category name
  if (!categoryName) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Категория не найдена</Alert>
      </Container>
    );
  }
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <Container className="py-5">
      {/* Хлебные крошки */}
      <Breadcrumb className="mb-4">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
          Главная
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{categoryName}</Breadcrumb.Item>
      </Breadcrumb>
      
      {/* Заголовок категории */}
      <div className="mb-5">
        <h1 className="display-5 fw-bold mb-3">{categoryName}</h1>
        {currentCategory && currentCategory.description && (
          <p className="lead text-muted">{currentCategory.description}</p>
        )}
      </div>
      
      {/* News list */}
      {items.length > 0 ? (
        <Row className="g-4">
          {items.map((item) => (
            <Col key={item.id} md={4}>
              <Card className="h-100 shadow-sm">
                {item.image_url && (
                  <Card.Img 
                    variant="top" 
                    src={item.image_url} 
                    alt={item.title}
                    style={{ height: '180px', objectFit: 'cover' }}
                  />
                )}
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    {item.category && (
                      <Badge bg="primary">
                        {item.category.name}
                      </Badge>
                    )}
                    <small className="text-muted">
                      {formatDate(item.created_at)}
                    </small>
                  </div>
                  <Card.Title>{item.title}</Card.Title>
                  <Card.Text className="text-muted">
                    {item.short_description}
                  </Card.Text>
                  <Link to={`/news/${item.id}`} className="stretched-link">
                    Читать далее
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : status === 'succeeded' ? (
        <Alert variant="info">В этой категории пока нет новостей.</Alert>
      ) : (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
    </Container>
  );
};

export default CategoryPage;
